import { graphql } from 'app/generated/gql'
import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useQuery, useMutation } from 'urql'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { useCopyToClipboard } from 'app/hooks/useCopyToClipboard'
import type { LocationType } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { TabType } from 'app/components/ui/nav-tabs'

const enum Sections {
  AVAILABILITY = `availability`,
  APPOINTMENT_TYPES = `appointment-types`,
  SETTINGS = `settings`,
}

const getTabs = (): TabType[] => [
  { name: `Availability`, href: Sections.AVAILABILITY },
  { name: `Appointment Types`, href: Sections.APPOINTMENT_TYPES },
  { name: `Settings`, href: Sections.SETTINGS },
]

const SCHEDULING_LOCATION_QUERY = graphql(`
  query SchedulingLocation($locationId: ID!) {
    location(id: $locationId) {
      id
      name
      isSchedulerLive
      products
    }
  }
`)

const TOGGLE_SCHEDULER_LIVE_MUTATION = graphql(`
  mutation ToggleSchedulerLive($id: ID!, $input: UpdateLocationInput!) {
    updateLocation(id: $id, input: $input) {
      id
      isSchedulerLive
    }
  }
`)

export type UseSchedulingLocationViewReturn = {
  location: LocationType
  tabs: TabType[] | undefined
  schedulingUrl: string
  onToggleSchedulerLive: (value: boolean) => Promise<boolean>
  onCopyPreviewLink: () => void
  onCopySchedulingLink: () => void
}

export function useSchedulingLocationView(): UseSchedulingLocationViewReturn {
  const { locationId } = useParams()
  const [location, setLocation] = useState<LocationType>({} as LocationType)
  const [confirm] = useConfirmation()
  const copyToClipboard = useCopyToClipboard()

  const [result] = useQuery({
    query: SCHEDULING_LOCATION_QUERY,
    variables: { locationId: locationId! },
    pause: !locationId,
  })

  useEffect(() => {
    if (result.data?.location) {
      setLocation(result.data.location as LocationType)
    }
  }, [result.data])

  const [, executeToggle] = useMutation(TOGGLE_SCHEDULER_LIVE_MUTATION)

  const availabilitySettingsEnabled = !!(location as any).products?.includes?.(`Scheduler`)

  const schedulingUrl = `${window.location.origin}/scheduling/${location.id || locationId}`

  const onToggleSchedulerLive = async (value: boolean) => {
    return confirm({
      title: `Change Scheduler Live Status`,
      description: value
        ? `Are you sure you want to turn on the Scheduler?`
        : `Are you sure you want to disable the Scheduler?`,
      variant: `danger`,
    })
      .then(async () => {
        const res = await executeToggle({ id: locationId!, input: { isSchedulerLive: value } })
        if (!res.error) {
          notifySuccess(`Scheduler status updated`)
          setLocation((prev) => ({ ...prev, isSchedulerLive: value }))
          return true
        } else {
          notifyError(`Scheduler live status could not be updated`)
          return false
        }
      })
      .catch(() => false)
  }

  return {
    location,
    tabs: availabilitySettingsEnabled ? undefined : getTabs(),
    schedulingUrl,
    onToggleSchedulerLive,
    onCopyPreviewLink: () =>
      copyToClipboard(`${schedulingUrl}?preview=true`, `Copied preview link to clipboard`, `copy-preview-to-clipboard`),
    onCopySchedulingLink: () => copyToClipboard(schedulingUrl, `Copied to clipboard`, `copy-to-clipboard`),
  }
}
